import React, { useState } from 'react';
import { moneyOrAbsent } from '../utils/moneyOrAbsent';
import useMeasuredWidth from '../hooks/useMeasuredWidth';

/**
 * Coerce a possibly-absent/malformed value into a safe number for chart
 * SCALE arithmetic (a bar's width or height, or a `Math.max` scan across a
 * whole dataset) — NEVER for its label text.
 *
 * 🚨 **WHY THIS EXISTS.** `Math.max` returns `NaN` the instant ANY argument
 * is `NaN`, and `undefined`/a non-numeric string coerce to `NaN` on the way
 * in — so a single bad value used to poison `maxValue` for a WHOLE chart,
 * and with it every bar's width/height, not just the bad bar's own. Absent
 * (`null`/`undefined`/`''`) and malformed (present but non-finite) both
 * contribute `0` here, exactly like a real, deliberate `0` would — they
 * still get their own honest label via `moneyOrAbsent` at the call site,
 * this function only keeps the GEOMETRY finite.
 *
 * Shared by `BarChart.js` and `VerticalBarChart.js` (imported, not
 * re-implemented — Code Unification Principle, root `CLAUDE.md`). Defined
 * here rather than under `utils/` because that tree is under active,
 * unrelated edits by another agent at the time of this fix; it has no
 * caller outside these two sibling chart components, so a `utils/` home can
 * be revisited later without urgency.
 */
export function resolveScaleValue(val) {
  if (val == null || val === '') return 0; // absent
  const n = Number(val);
  return Number.isFinite(n) ? n : 0; // malformed
}

// ---------------------------------------------------------------------------
// 🎨 VALUE-LABEL PLACEMENT AND INK — 2026-10-01 contrast pass.
//
// The label used to sit INSIDE any bar wider than 20% of the track, always in
// white. Two defects, both measured on Dept Dashboard › Team Spend by Supplier:
//   1. white on the house chart palette fails 1.4.3 almost everywhere —
//      amber #F59E0B 2.15:1, teal #14B8A6 2.49:1, green #10B981 2.54:1, even
//      blue #3B82F6 3.68:1 (12px medium text needs 4.5:1);
//   2. "20% of the track" says nothing about whether `£12,345.67` FITS — on a
//      narrow card a 22% bar is ~40px, so the figure overflowed the bar's
//      rounded left edge and its first characters were lost.
// Now: the ink is whichever of white / gray-950 contrasts more with THIS bar's
// fill (every colour in constants/colors.js's chart palettes clears 4.5:1 with
// one of them), and the label goes inside only when it FITS and PASSES —
// otherwise just past the bar's end, in the ordinary chart text colour on the
// track. The ink is an inline hex, never a Tailwind class: `html.dark
// .text-gray-900 { … !important }` would repaint a class-based dark ink
// near-white on an unchanged bar fill.
// ---------------------------------------------------------------------------
const INK_LIGHT = '#ffffff';
const INK_DARK = '#030712'; // gray-950
const MIN_TEXT_CONTRAST = 4.5; // WCAG 1.4.3, normal-size text (the label is 12px)

function parseHex(color) {
  if (typeof color !== 'string') return null;
  let h = color.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h)) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
}

function relativeLuminance(rgb) {
  const [r, g, b] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours, or `null` if either will not parse. */
export function contrastRatio(a, b) {
  const ra = parseHex(a);
  const rb = parseHex(b);
  if (!ra || !rb) return null;
  const la = relativeLuminance(ra);
  const lb = relativeLuminance(rb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * `{ ink, ratio, passes }` for a label drawn ON `fill`. An unparseable fill
 * (a named colour, an `rgb()`) reports `passes: false`, so the label goes
 * outside the bar rather than guessing.
 */
export function barLabelInk(fill) {
  const light = contrastRatio(fill, INK_LIGHT);
  const dark = contrastRatio(fill, INK_DARK);
  if (light == null || dark == null) return { ink: INK_LIGHT, ratio: null, passes: false };
  const ink = light >= dark ? INK_LIGHT : INK_DARK;
  const ratio = Math.max(light, dark);
  return { ink, ratio, passes: ratio >= MIN_TEXT_CONTRAST };
}

// A conservative estimate of a 12px font-medium label's width — wider than the
// real glyphs, so a borderline label errs OUTSIDE (fully readable) rather than
// inside and clipped. Includes the bar's pr-2 and a little breathing room.
const LABEL_CHAR_PX = 7;
const LABEL_INSIDE_PAD_PX = 16;
const LABEL_OUTSIDE_GAP_PX = 6;

/**
 * `'inside' | 'outside'` for one bar's value label.
 *
 * `trackPx` is the measured track width, or `null` before the first
 * measurement — then the old proportional rule (inside above 20%) stands in,
 * still gated on contrast. A label that would not fit OUTSIDE either (a long
 * bar whose fill passes with neither ink — no house palette colour does) stays
 * inside rather than being clipped off the track's end.
 */
export function barLabelPlacement({ widthPercent, text, fill, trackPx }) {
  const { passes } = barLabelInk(fill);
  if (trackPx == null) return passes && widthPercent > 20 ? 'inside' : 'outside';
  const barPx = (trackPx * Math.max(widthPercent, 5)) / 100;
  const labelPx = String(text).length * LABEL_CHAR_PX;
  if (passes && barPx >= labelPx + LABEL_INSIDE_PAD_PX) return 'inside';
  if (trackPx - barPx >= labelPx + LABEL_OUTSIDE_GAP_PX) return 'outside';
  return 'inside';
}

/**
 * SVG Horizontal Bar Chart component
 * @param {Object} props
 * @param {Array} props.data - Array of { label, value, color }
 * @param {number} props.height - Chart height in pixels (auto if not specified)
 * @param {string} props.title - Optional chart title
 * @param {string} props.valuePrefix - Prefix for values (e.g. '£')
 * @param {number} props.maxBars - Maximum bars to show (default 8)
 * @param {Function} props.onBarClick - Optional callback when a bar is clicked, receives (item, index)
 */
function BarChart({ data, title, valuePrefix = '', maxBars = 8, onBarClick }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Width of the chart; every row's track is that minus the label column and
  // the row's gap-2. One measurement serves every bar. 🪤 The ref goes on the
  // ROOT div of BOTH returns: the dashboards mount this with `data` empty
  // (the "No data" branch) and fill it after the fetch, and the hook measures
  // and observes only what its ref held at mount — on a branch-only node the
  // width stayed `null` forever and every bar fell back to the 20% rule. Both
  // roots are a <div> at the same position, so React keeps the one DOM node.
  const [rootRef, rootWidth] = useMeasuredWidth();

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col" ref={rootRef}>
        {title && <h4 className="text-sm font-semibold text-gray-600 mb-2">{title}</h4>}
        <div className="text-gray-400 text-sm text-center py-4">No data</div>
      </div>
    );
  }

  const displayData = data.slice(0, maxBars);
  // The trailing `, 1` is load-bearing, not decorative: if every bar's value
  // is absent/malformed, `displayData.map(resolveScaleValue)` is all `0`s,
  // and `Math.max(0, 0, …)` is `0` — dividing by THAT reintroduces NaN from
  // the other end. `, 1` keeps `maxValue` a positive number even then.
  const maxValue = Math.max(...displayData.map(d => resolveScaleValue(d.value)), 1);
  const barHeight = 28;
  const gap = 6;
  const labelWidth = 120;
  const trackPx = rootWidth ? Math.max(rootWidth - labelWidth - 8, 0) : null;

  // 🚨 **AN ABSENT VALUE IS NOT A ZERO.** ⚖️ Owner ruling 2026-09-21: *"Guard
  // the absence marker AT THE CALL SITES. Leave `formatCurrency` a dumb
  // converter."* This is a standalone display — a label on a bar — so it takes
  // the em dash, the marker `utils/orderListPdf.js` already uses for a money
  // cell with nothing in it (and which that file converts a plain `'-'` INTO,
  // which is why the dash and not the hyphen).
  //
  // 🪤 **REACHABLE, and the arithmetic is why it is easy to miss.** Four live
  // `valuePrefix={'£'}` charts are fed by `getSupplierBarData` /
  // `getUserBarData` on the admin and department dashboards, which map
  // `s.total_spend` through RAW — and a SQL `SUM()` over no rows is `NULL`.
  // `null / maxValue` is `0`, so `widthPercent` is a real `0`, the `<= 20`
  // branch renders, and the bar printed a confident `£0.00` for a supplier
  // whose spend nobody had recorded. (`undefined` behaves differently — it
  // makes `widthPercent` NaN and neither label branch fires at all — so
  // `null`/`''` are the states that actually reached a user.)
  //
  // 🔑 The three states — absent / malformed / the money, and why a real `0`
  // is never an absence — are defined once in `utils/moneyOrAbsent.js`.
  const formatValue = (val) => {
    if (valuePrefix === '£') {
      return moneyOrAbsent(val);
    }
    return valuePrefix + val;
  };

  return (
    <div className="flex flex-col w-full" ref={rootRef}>
      {title && <h4 className="text-sm font-semibold text-gray-600 mb-3">{title}</h4>}
      <div className="space-y-1">
        {displayData.map((item, i) => {
          const widthPercent = (resolveScaleValue(item.value) / maxValue) * 100;
          const fill = item.color || '#3B82F6';
          const text = formatValue(item.value);
          const placement = barLabelPlacement({ widthPercent, text, fill, trackPx });
          return (
            <div
              key={i}
              className={`flex items-center gap-2 group ${onBarClick ? 'cursor-pointer' : ''}`}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => onBarClick && onBarClick(item, i)}
              role={onBarClick ? 'button' : undefined}
              tabIndex={onBarClick ? 0 : undefined}
              onKeyDown={(e) => { if (onBarClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onBarClick(item, i); } }}
            >
              <div
                className="text-xs text-gray-600 text-right truncate flex-shrink-0"
                style={{ width: labelWidth }}
                title={item.label}
              >
                {item.label}
              </div>
              <div className="flex-1 relative bg-gray-100 rounded-full overflow-hidden" style={{ height: barHeight }}>
                <div
                  className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-300"
                  style={{
                    width: `${Math.max(widthPercent, 5)}%`,
                    backgroundColor: fill,
                    opacity: hoveredIndex !== null && hoveredIndex !== i ? 0.6 : 1,
                  }}
                >
                  {placement === 'inside' && (
                    <span
                      className="text-xs font-medium whitespace-nowrap"
                      style={{ color: barLabelInk(fill).ink }}
                      data-testid="bar-value-label"
                      data-placement="inside"
                    >
                      {text}
                    </span>
                  )}
                </div>
                {placement === 'outside' && (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                    style={{ left: `calc(${Math.max(widthPercent, 5)}% + ${LABEL_OUTSIDE_GAP_PX}px)` }}
                    data-testid="bar-value-label"
                    data-placement="outside"
                  >
                    {text}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {data.length > maxBars && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          Showing top {maxBars} of {data.length}
        </p>
      )}
    </div>
  );
}

export default BarChart;
